/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function changeVariant(e, elem, id, price){
  e.preventDefault();
  e.stopPropagation();
  var quickAddInfo = elem.closest(".quick-add-info");
  if (!quickAddInfo) {
    return;
  }
  var variantPrice = price;
  if(price.toString().length < 4){
    variantPrice = price + "00";
  }
  variantPrice = formatMoney(variantPrice);

  var sizePicker = elem.closest(".quick-add-sizes");
  var selectedLabel = elem.id ? sizePicker.querySelector('label[for="' + elem.id + '"]') : elem;
  var optionLabel = elem.dataset.optionLabel || elem.textContent.trim();
  sizePicker.querySelectorAll(".select-mb label, .select-mb span").forEach(function(option) {
    option.classList.remove("selected");
  });
  if (selectedLabel) {
    selectedLabel.classList.add("selected");
  }
  sizePicker.querySelector(".current_value_m").textContent = optionLabel;
  quickAddInfo.querySelector(".variantId").value = id;
  closeAllQuickSelects();
  var productCard = quickAddInfo.closest("product-item, .boost-pfs-filter-product-item");
  var priceElement = productCard && productCard.querySelector(".money");
  if (priceElement) {
    priceElement.innerHTML = variantPrice;
  }
  var fromLabel = productCard && productCard.querySelector(".from-label");
  if (fromLabel) {
    fromLabel.remove();
  }
}

function toggleQuickSelect(elem){
  var sizePicker = elem.closest(".quick-add-sizes");
  var el = sizePicker && sizePicker.querySelector(".select-mb");
  if (!el) {
    return;
  }
  var willOpen = !el.classList.contains("open");
  
  if(willOpen){
    closeAllQuickSelects();
    el.classList.add("open");
  }else{
    el.classList.remove("open");
  }
  elem.setAttribute("aria-expanded", willOpen ? "true" : "false");
}

function closeAllQuickSelects(){
  const nodeList = document.querySelectorAll(".select-mb");
  for (let i = 0; i < nodeList.length; i++) {
    nodeList[i].classList.remove("open");
  }
  document.querySelectorAll(".quick-add-sizes .current_value_m[aria-expanded]").forEach(function(trigger) {
    trigger.setAttribute("aria-expanded", "false");
  });
}

function handleQuickSelectKeydown(event, elem) {
  var sizePicker = elem.closest(".quick-add-sizes");
  var trigger = sizePicker && sizePicker.querySelector(".current_value_m");
  var options = sizePicker && sizePicker.querySelector(".select-mb");
  if (!trigger || !options) {
    return;
  }
  if (event.key === "Escape" && options.classList.contains("open")) {
    event.preventDefault();
    event.stopPropagation();
    closeAllQuickSelects();
    trigger.focus();
  } else if (event.key === "ArrowDown" && elem === trigger) {
    event.preventDefault();
    event.stopPropagation();
    if (!options.classList.contains("open")) {
      toggleQuickSelect(trigger);
    }
    var selectedOption = options.querySelector("input:checked:not(:disabled)") || options.querySelector("input:not(:disabled)");
    if (selectedOption) {
      selectedOption.focus();
    }
  }
}

$(window).click(function() {
  closeAllQuickSelects();
});

$(".block-swatch__radio").click(function(){
  var value = $(this).attr("value");
  if(value == "Custom Value"){
    $(".block-swatch-custom").addClass("selected");
    $(".product-meta__price-list-container").css("display","none");
  }else{
    $(".block-swatch-custom").removeClass("selected");
    $(".product-meta__price-list-container").css("display","flex");
  }
});

function addToWishlist(){
  e.preventDefault();
  document.getElementById("btnAddToWishlist").querySelector('input').click();
}

// js/helper/currency.js
function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

// document.addEventListener('cart:updated', function(event) {
//   var giftwrap_oncart = false;
//   var cart = event.detail.cart; // Get the updated content of the cart
//   var giftwrap_handle = "gift-wrap-1";
//   var giftwrap_id = 0;
// }, function(){
//   $.each(cart.items, function(i, cart_item) {
//     if (cart_item.handle == giftwrap_handle) {
//       giftwrap_oncart = true;
//       giftwrap_id = cart_item.id;
//     }
//   });
//   if(!giftwrap_oncart){
//     $(".mini-cart__recommendations-inner").css("background-color","red")
//     $(".giftwrap-by-wrapped label").click();
//     // jQuery.ajax({
//     //   type: 'POST',
//     //   url: '/cart/update.js', 
//     //   data: { updates: { giftwrap_id: 0 }, attributes: { 'gift-wrapping': '' } }, 
//     //   dataType: 'json',
//     //   success: function() { location.href = '/cart'; }
//     // });
//   }
// });
